/**
 * La ricerca globale (03-SCHERMATE-TV §3.9): canali, programmi, film e serie in un colpo solo.
 *
 * ## Non chiede niente a nessuno
 *
 * Tutto quello che serve è **già sull'apparecchio**: il listino dei canali, l'EPG della finestra
 * corrente, i film e le serie stanno in `localStorage` con le loro scadenze a ore d'uso. Una ricerca
 * che chiedesse al gateway a ogni lettera sarebbe una richiesta per tasto premuto — su un catalogo
 * da undicimila titoli e con il limite di consegna del gateway a 3000 ogni quarto d'ora
 * (`project_ban_del_gateway`), è il modo più veloce di farsi bloccare l'indirizzo.
 *
 * Il prezzo è che si cerca in quel che si ha: i programmi sono quelli della finestra caricata, non
 * tutta la settimana. Chi disegna lo dice, invece di far credere che il catalogo sia più piccolo di
 * com'è.
 *
 * ## Il confronto è senza accenti e senza maiuscole
 *
 * Chi scrive col telecomando non mette gli accenti, e le tastiere a schermo di questo prodotto non
 * ne hanno: cercare «perche» deve trovare «Perché». Si scompone in NFD e si tolgono i segni
 * combinanti — con una fascia di punti di codice esplicita, **non** con `\p{M}`, che è di Chromium
 * 64 mentre Tizen 5 monta il 63 (vedi `tizen5.test.ts`).
 */

import { compareCatalogNames } from './catalogSort.js';
import type { VodItem } from './catalog.js';
import type { Channel, Program } from './guide.js';

/** Le schede del filtro. `all` non è una scheda come le altre: è l'unione, con un tetto per sezione. */
export type SearchTab = 'all' | 'tv' | 'movie' | 'series' | 'channel';

/**
 * Quanti risultati per sezione nella scheda «Tutti».
 *
 * Otto, come nel prototipo. Il punto della scheda «Tutti» è far vedere **che tipo** di risultati
 * esistono, non elencarli: chi vuole l'elenco intero passa alla scheda di quel tipo, e il conteggio
 * accanto al nome gli dice quanto trova.
 */
export const SEARCH_SECTION_LIMIT = 8;

/**
 * Da quante lettere si cerca davvero.
 *
 * Con una lettera sola su un catalogo vero rispondono migliaia di titoli, cioè un elenco che non
 * significa niente e che il televisore fatica a disegnare. Due è il minimo che restringe.
 */
export const SEARCH_MIN_LENGTH = 2;

export interface SearchInput {
  readonly query: string;
  readonly channels: ReadonlyArray<Channel>;
  /** I programmi che si hanno in archivio: la finestra caricata, non tutta la settimana. */
  readonly programs: ReadonlyArray<Program>;
  readonly vod: ReadonlyArray<VodItem>;
  readonly nowMs: number;
}

export interface SearchResults {
  /** Programmi non ancora finiti: prima quelli in onda, poi per orario. */
  readonly tv: ReadonlyArray<Program>;
  readonly movies: ReadonlyArray<VodItem>;
  readonly series: ReadonlyArray<VodItem>;
  readonly channels: ReadonlyArray<Channel>;
  readonly total: number;
}

/** Il testo confrontabile: minuscolo, senza accenti, senza spazi ai bordi. */
export function normalizeForSearch(text: string): string {
  return text
    .normalize('NFD')
    // I segni combinanti del blocco latino: l'accento di «é» dopo la scomposizione è U+0301.
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

function matches(haystack: string, needle: string): boolean {
  return normalizeForSearch(haystack).includes(needle);
}

/**
 * Se un canale risponde alla ricerca.
 *
 * Due strade, e la seconda è quella che si dimentica: **il numero**. Chi cerca «10» sul telecomando
 * quasi sempre vuole il canale 10, non un titolo che contiene «10» — quindi il numero si confronta
 * con `startsWith` e non con `includes`, o cercando «1» uscirebbero il 1, il 21, il 100 e il 171
 * nello stesso mucchio.
 */
function channelMatches(channel: Channel, needle: string): boolean {
  if (matches(channel.name, needle)) return true;
  return String(channel.number).startsWith(needle);
}

/**
 * Se un programma risponde.
 *
 * Anche sulla **tipologia** dell'evento — `calcio`, `tennis` — perché è la parola che la gente usa:
 * cercando «calcio» ci si aspetta le partite, e il titolo di una partita è «Roma – Torino».
 */
function programMatches(program: Program, needle: string): boolean {
  if (matches(program.title, needle)) return true;
  if (matches(program.genre, needle)) return true;
  return program.eventKind !== null && matches(program.eventKind, needle);
}

function vodMatches(item: VodItem, needle: string): boolean {
  return matches(item.title, needle) || matches(item.genre, needle);
}

export function isLive(program: Program, nowMs: number): boolean {
  return program.startMs <= nowMs && nowMs < program.endMs;
}

const EMPTY: SearchResults = { tv: [], movies: [], series: [], channels: [], total: 0 };

/**
 * Cerca. Sotto la lunghezza minima non risponde **niente**, e non è lo stesso che «nessun
 * risultato»: chi disegna mostra le ricerche recenti, non il messaggio di elenco vuoto.
 */
export function searchCatalog(input: SearchInput): SearchResults {
  const needle = normalizeForSearch(input.query);
  if (needle.length < SEARCH_MIN_LENGTH) return EMPTY;

  const tv: Program[] = [];
  for (const program of input.programs) {
    // I programmi già finiti non si mostrano: «era in onda ieri» non è un risultato su cui si possa
    // premere OK, e riempirebbe l'elenco spingendo fuori quel che si può guardare.
    if (program.endMs <= input.nowMs) continue;
    if (programMatches(program, needle)) tv.push(program);
  }
  /*
    Prima quel che è in onda, poi per orario.

    In onda vuol dire «premi OK e lo stai guardando»; futuro vuol dire «premi OK e ti avviso». Sono
    due azioni diverse, e mescolarle per orario metterebbe una partita in corso sotto tre programmi
    che cominciano fra due ore.
  */
  tv.sort((left, right) => {
    const leftLive = isLive(left, input.nowMs);
    const rightLive = isLive(right, input.nowMs);
    if (leftLive !== rightLive) return leftLive ? -1 : 1;
    return left.startMs - right.startMs;
  });

  const movies: VodItem[] = [];
  const series: VodItem[] = [];
  for (const item of input.vod) {
    if (!vodMatches(item, needle)) continue;
    if (item.kind === 'series') series.push(item);
    else movies.push(item);
  }
  const byTitle = (left: VodItem, right: VodItem): number =>
    compareCatalogNames(left.title, right.title);
  movies.sort(byTitle);
  series.sort(byTitle);

  const channels: Channel[] = [];
  for (const channel of input.channels) {
    if (channelMatches(channel, needle)) channels.push(channel);
  }
  // Per numero, che è l'ordine in cui la gente ha i canali in testa.
  channels.sort((left, right) => left.number - right.number);

  return {
    tv,
    movies,
    series,
    channels,
    total: tv.length + movies.length + series.length + channels.length,
  };
}

export interface SearchTabCount {
  readonly tab: SearchTab;
  readonly count: number;
}

/**
 * Le schede da mostrare, col loro conteggio.
 *
 * **Solo quelle che hanno risultati**, come nel prototipo: una scheda «Canali · 0» è un posto dove
 * si può andare per trovare il vuoto, e su un telecomando ogni passo a vuoto costa. «Tutti» c'è solo
 * se c'è almeno qualcosa, e non si mostra da sola quando esiste un tipo solo — sarebbe due nomi per
 * lo stesso elenco.
 */
export function searchTabs(results: SearchResults): ReadonlyArray<SearchTabCount> {
  const each: ReadonlyArray<SearchTabCount> = [
    { tab: 'tv', count: results.tv.length },
    { tab: 'movie', count: results.movies.length },
    { tab: 'series', count: results.series.length },
    { tab: 'channel', count: results.channels.length },
  ];
  const withResults = each.filter((entry) => entry.count > 0);
  if (withResults.length <= 1) return withResults;
  return [{ tab: 'all', count: results.total }, ...withResults];
}

/** I risultati da disegnare in una scheda: «Tutti» taglia ogni sezione, le altre mostrano tutto. */
export function resultsForTab(results: SearchResults, tab: SearchTab): SearchResults {
  if (tab === 'all') {
    return {
      tv: results.tv.slice(0, SEARCH_SECTION_LIMIT),
      movies: results.movies.slice(0, SEARCH_SECTION_LIMIT),
      series: results.series.slice(0, SEARCH_SECTION_LIMIT),
      channels: results.channels.slice(0, SEARCH_SECTION_LIMIT),
      total: results.total,
    };
  }
  return {
    tv: tab === 'tv' ? results.tv : [],
    movies: tab === 'movie' ? results.movies : [],
    series: tab === 'series' ? results.series : [],
    channels: tab === 'channel' ? results.channels : [],
    total: results.total,
  };
}

/** Quante ricerche recenti si ricordano. */
export const RECENT_SEARCHES_LIMIT = 8;

/**
 * L'elenco delle ricerche recenti dopo averne fatta una.
 *
 * La più recente davanti, **senza doppioni**: chi cerca «serie a» tre volte in un pomeriggio non
 * vuole tre pastiglie uguali. Il confronto è quello normalizzato, ma si conserva il testo **come
 * l'ha scritto chi cercava**: è quello che riconosce.
 */
export function rememberSearch(
  recent: ReadonlyArray<string>,
  query: string,
  limit: number = RECENT_SEARCHES_LIMIT,
): ReadonlyArray<string> {
  const trimmed = query.trim();
  if (normalizeForSearch(trimmed).length < SEARCH_MIN_LENGTH) return recent;
  const needle = normalizeForSearch(trimmed);
  const without = recent.filter((entry) => normalizeForSearch(entry) !== needle);
  return [trimmed, ...without].slice(0, limit);
}
